import { API_BASE_URL, apiClient } from '../../lib/apiClient';
import type {
  CaptionOptionsRead,
  CaptionUpdate,
  DraftVariantRead,
  FlagCreate,
  FlagRead,
  GenerationRequestCreate,
  GenerationRequestDetailRead,
  GenerationRequestRead,
  GenerationRequestStatus,
  GenerationRequestSummaryRead,
  GenerationRequestThemeUpdate,
  IconRead,
  IntentRead,
  PictureStyleRead,
  PublishedPostCreate,
  PublishJobRead,
  PublishedPostRead,
  RedesignCreate,
  RejectionCreate,
  RevisionCreate,
  SlideLayerEditUpdate,
  SlideRead,
  SlideThemeCatalogRead,
  SseTokenRead,
  VariantSelectionCreate,
  VibeRead,
} from '../../types/api';

/**
 * The generation-request resource: creating a run, reading it back, minting a
 * stream token, selecting a variant, editing slide layers and the two terminal
 * decisions — approve and reject.
 *
 * Both review steps live in `features/review` but act on this same resource, so
 * the calls stay here rather than being split across two clients.
 */

/** Pipeline routes are business-scoped, like every other owned resource (SRS §4). */
function requestsPath(businessId: string): string {
  return `/api/v1/businesses/${businessId}/generation-requests`;
}

/** Shared lookup — the seeded vibes (funny, trendy, professional). */
export function fetchVibes(signal?: AbortSignal): Promise<VibeRead[]> {
  return apiClient.get<VibeRead[]>('/api/v1/vibes', { signal });
}

/**
 * Shared lookup — the post-intent registry, in the server's own order.
 *
 * Global like {@link fetchVibes}: the vocabulary is code on the backend (each
 * key carries a prompt recipe), so the SPA reads it rather than spelling it
 * out. Every surface that offers intents goes through `useIntents`.
 */
export function fetchIntents(signal?: AbortSignal): Promise<IntentRead[]> {
  return apiClient.get<IntentRead[]>('/api/v1/intents', { signal });
}

/**
 * Shared lookup — the curated icons a slide may carry, grouped by category in
 * the server's curated order. Global like {@link fetchIntents}: the list is
 * code on the backend. Every surface goes through `useIcons`.
 */
export function fetchIcons(signal?: AbortSignal): Promise<IconRead[]> {
  return apiClient.get<IconRead[]>('/api/v1/icons', { signal });
}

/**
 * Shared lookup — the caption lengths (with each platform's character range)
 * and caption styles the brief can ask for.
 *
 * Global like {@link fetchIntents}: the vocabulary and the ranges are code on
 * the backend, so the brief shows the server's numbers and never its own.
 * Every surface goes through `useCaptionOptions`.
 */
export function fetchCaptionOptions(signal?: AbortSignal): Promise<CaptionOptionsRead> {
  return apiClient.get<CaptionOptionsRead>('/api/v1/caption-options', { signal });
}

/**
 * Shared lookup — the picture styles (the medium AI pictures are made in), in
 * registry order. Global like {@link fetchIntents}. Every surface goes through
 * `usePictureStyles`.
 */
export function fetchPictureStyles(signal?: AbortSignal): Promise<PictureStyleRead[]> {
  return apiClient.get<PictureStyleRead[]>('/api/v1/picture-styles', { signal });
}

/**
 * The slide-theme catalogue — the looks a rendered post can be drawn in: a
 * carousel's slides, or the one square of a generated single.
 *
 * Global rather than business-scoped, like {@link fetchVibes}: the looks are
 * seeded by the deployment, and a business picks from them rather than
 * authoring its own. `vibeId` narrows the list to the looks tagged for that
 * vibe; omit it for everything, in catalogue order.
 */
export function listSlideThemes(
  vibeId?: string | null,
  signal?: AbortSignal,
): Promise<SlideThemeCatalogRead[]> {
  return apiClient.get<SlideThemeCatalogRead[]>('/api/v1/slide-themes', {
    query: { vibe: vibeId ?? undefined },
    signal,
  });
}

/**
 * Queues a pipeline run. Domain failures arrive as `ApiError.code`:
 * `POST_TYPE_NOT_AVAILABLE` (409), `ASSET_NOT_FOUND`, `QUEUE_UNAVAILABLE` (503).
 */
export function createGenerationRequest(
  businessId: string,
  body: GenerationRequestCreate,
): Promise<GenerationRequestRead> {
  return apiClient.post<GenerationRequestRead>(requestsPath(businessId), { body });
}

/** One page of the request log. `limit` is capped at 100 server-side. */
export interface RequestPageQuery {
  limit: number;
  offset: number;
  /**
   * Only requests in one of these states. Sent as a repeated `status` param;
   * absent or empty means every state, as before the filter existed.
   */
  status?: readonly GenerationRequestStatus[];
}

/**
 * This business's requests, newest first — the history log.
 *
 * A bare array, not an envelope: the backend pages by `limit`/`offset` and does
 * not count the total, so "is there more" is "did this page come back full".
 * Variants and slides are not embedded; the review screen reads those per id.
 * Each row carries a thumbnail path and a caption preview — on a deploy that
 * has them; see `GenerationRequestSummaryRead`.
 */
export function fetchGenerationRequests(
  businessId: string,
  page: RequestPageQuery,
  signal?: AbortSignal,
): Promise<GenerationRequestSummaryRead[]> {
  return apiClient.get<GenerationRequestSummaryRead[]>(requestsPath(businessId), {
    query: {
      limit: page.limit,
      offset: page.offset,
      status: page.status && page.status.length > 0 ? page.status : undefined,
    },
    signal,
  });
}

/**
 * One request with its `variants` and `slides`. 404 once the id is not this
 * business's. Also the poll target while a slide re-render is in flight.
 */
export function fetchGenerationRequest(
  businessId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<GenerationRequestDetailRead> {
  return apiClient.get<GenerationRequestDetailRead>(`${requestsPath(businessId)}/${requestId}`, {
    signal,
  });
}

/**
 * Mints the short-lived token (300s) that authenticates one SSE subscription.
 *
 * Called through the authenticated client, so the session token stays in the
 * Authorization header. Only the minted token is ever allowed in a URL.
 */
export function mintSseToken(
  businessId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<SseTokenRead> {
  return apiClient.post<SseTokenRead>(`${requestsPath(businessId)}/${requestId}/sse-token`, {
    signal,
  });
}

/**
 * Review Step 1: commits the chosen caption plus answers to that variant's
 * clarifying questions. Domain failures: `REQUEST_NOT_AWAITING_REVIEW` (409),
 * `VARIANT_NOT_FOUND` (404), `QUESTIONS_UNANSWERED` (422).
 *
 * A request with nothing to render — a single image on the user's own asset —
 * comes back `complete`. Anything with a visual phase (`hasVisualPhase`: a
 * carousel, or a single image briefed with `image_asset_id: null`) comes back
 * `designing`: the visual phase is enqueued server-side and streams its stages
 * on the same channel, so the caller resubscribes on a fresh token.
 */
export function selectVariant(
  businessId: string,
  requestId: string,
  body: VariantSelectionCreate,
): Promise<GenerationRequestDetailRead> {
  return apiClient.post<GenerationRequestDetailRead>(
    `${requestsPath(businessId)}/${requestId}/select-variant`,
    { body },
  );
}

/**
 * Replaces one variant's caption with the user's own words.
 *
 * Free and deterministic, like a layer edit: no model call and nothing off the
 * monthly quota. The text still goes past the same copy guardrails the pipeline
 * applies, so a never-say term or an over-limit caption comes back as 422
 * `CAPTION_REJECTED` with a human sentence in `detail` — the caller shows that
 * sentence rather than inventing its own.
 *
 * Legal at `awaiting_review` for either variant, and at `awaiting_final_review`
 * for the chosen one only. Domain failures: `REQUEST_NOT_EDITABLE` (409),
 * `VARIANT_NOT_SELECTED` (409), `VARIANT_NOT_FOUND` (404).
 */
export function updateVariantCaption(
  businessId: string,
  requestId: string,
  variantId: string,
  body: CaptionUpdate,
): Promise<DraftVariantRead> {
  return apiClient.put<DraftVariantRead>(
    `${requestsPath(businessId)}/${requestId}/variants/${variantId}/caption`,
    { body },
  );
}

/**
 * Constrained layer edit on one slide: text, background, logo position. Never
 * an LLM call — the slide is re-rendered from the same brand template.
 *
 * The response carries `rendered_path: null` while that re-render job runs, so
 * the caller polls {@link fetchGenerationRequest} until it comes back non-null.
 * Domain failures: `REQUEST_NOT_EDITABLE` (409, not at final review),
 * `SLIDE_NOT_FOUND` / `ASSET_NOT_FOUND` (404).
 */
export function updateSlideLayers(
  businessId: string,
  requestId: string,
  slideId: string,
  body: SlideLayerEditUpdate,
): Promise<SlideRead> {
  return apiClient.put<SlideRead>(`${requestsPath(businessId)}/${requestId}/slides/${slideId}`, {
    body,
  });
}

/**
 * Re-renders already-drawn slides in a different look — a whole deck, or the
 * one square of a generated single.
 *
 * Every slide comes back with `rendered_path: null` and the new theme tokens,
 * so the caller polls exactly as it does after a layer edit. Like a layer edit
 * this is a pure re-render: no model call and nothing off the monthly quota.
 * The per-slide `overlay_style` and `card_enabled` tweaks are the theme's to
 * set, so they go back to what the new look says.
 *
 * Only legal at `awaiting_final_review`. Domain failures:
 * `REQUEST_NOT_EDITABLE` (409), `THEME_NOT_FOUND` (404).
 */
export function setRequestTheme(
  businessId: string,
  requestId: string,
  themeId: string,
): Promise<GenerationRequestDetailRead> {
  const body: GenerationRequestThemeUpdate = { theme_id: themeId };
  return apiClient.put<GenerationRequestDetailRead>(
    `${requestsPath(businessId)}/${requestId}/theme`,
    { body },
  );
}

/**
 * Review Step 2: accepts what was rendered — a deck or a generated single's one
 * square — as it stands. Only at `awaiting_final_review`, otherwise 409
 * `REQUEST_NOT_AWAITING_REVIEW`.
 */
export function approveRequest(
  businessId: string,
  requestId: string,
): Promise<GenerationRequestDetailRead> {
  return apiClient.post<GenerationRequestDetailRead>(
    `${requestsPath(businessId)}/${requestId}/approve`,
  );
}

/**
 * Rejects the current draft and returns the **new** request the regeneration
 * runs under — the caller navigates to it.
 *
 * Valid at `awaiting_review` and `awaiting_final_review`. A lineage allows three
 * regeneration attempts; the fourth returns 409 `REJECTION_CAP_REACHED`, and at
 * that point the user has exactly two ways forward (SRS invariant): accept the
 * current best, or layer-edit anything that was rendered.
 */
export function rejectRequest(
  businessId: string,
  requestId: string,
  body: RejectionCreate,
): Promise<GenerationRequestRead> {
  return apiClient.post<GenerationRequestRead>(`${requestsPath(businessId)}/${requestId}/reject`, {
    body,
  });
}

/**
 * Runs a failed request's brief again and returns the **new** request it runs
 * under — the caller navigates to it, as after a rejection.
 *
 * Not a rejection: the new row keeps the failed row's `attempt`, so a failure
 * never eats the rejection cap. Idempotent: a failed request that was already
 * retried answers with that retry (200) rather than queueing a second run (201).
 * The failed run was refunded server-side, so the net cost is one run.
 *
 * Domain failures: `REQUEST_NOT_FAILED` (409), `QUOTA_EXCEEDED` (429),
 * `QUEUE_UNAVAILABLE` (503).
 */
export function retryGenerationRequest(
  businessId: string,
  requestId: string,
): Promise<GenerationRequestRead> {
  return apiClient.post<GenerationRequestRead>(`${requestsPath(businessId)}/${requestId}/retry`);
}

/**
 * Asks the model to rewrite **one** caption on the reviewer's note, and answers
 * 202 with the request now in `revising`.
 *
 * The cheap half of the feedback loop: one structured model call over the same
 * knowledge base, this caption and this feedback. The brief, the other variant
 * and — at step 2 — the backdrops the run already drew are all kept, so nothing
 * is re-researched and no new art is bought. The worker puts the row back where
 * it came from (`awaiting_review`, or `designing` → `visual_guardrails` →
 * `awaiting_final_review` with the slides re-planned from the new caption), and
 * the caller resubscribes to the stream exactly as it does after selecting a
 * variant.
 *
 * A revision spends its own budget of five per lineage, not one of the three
 * regeneration attempts. Domain failures: `REQUEST_NOT_EDITABLE` (409, not at a
 * review step), `VARIANT_NOT_SELECTED` (409, at step 2 this is not the chosen
 * caption), `REVISION_CAP_REACHED` (409), `VARIANT_NOT_FOUND` (404),
 * `QUEUE_UNAVAILABLE` (503).
 */
export function reviseVariant(
  businessId: string,
  requestId: string,
  variantId: string,
  body: RevisionCreate,
): Promise<GenerationRequestRead> {
  return apiClient.post<GenerationRequestRead>(
    `${requestsPath(businessId)}/${requestId}/variants/${variantId}/revise`,
    { body },
  );
}

/**
 * Asks for the slides to be re-planned on the reviewer's note, and answers 202
 * with the request now in `designing`.
 *
 * The visual half of the feedback loop, and the answer to "the words are fine,
 * the pictures are not": the caption is untouched, the backdrops this run
 * already drew are reused, and the design call is the only model call spent —
 * no research, no rewrite, no new art. The run streams `designing` →
 * `visual_guardrails` → `awaiting_final_review` exactly as it did the first
 * time, so the caller applies the response through the same path a revision
 * takes and the stream resubscribes on a fresh token.
 *
 * It spends a revision, not one of the three regeneration attempts, and shares
 * the revision budget with {@link reviseVariant}. Only legal at
 * `awaiting_final_review`. Domain failures: `REQUEST_NOT_EDITABLE` (409),
 * `REVISION_CAP_REACHED` (409), `QUEUE_UNAVAILABLE` (503).
 */
export function redesignRequest(
  businessId: string,
  requestId: string,
  body: RedesignCreate,
): Promise<GenerationRequestRead> {
  return apiClient.post<GenerationRequestRead>(
    `${requestsPath(businessId)}/${requestId}/redesign`,
    {
      body,
    },
  );
}

/**
 * Reports one draft variant as wrong or unusable. Legal at every status, and it
 * mutates nothing: a flag never blocks, deletes or regenerates anything, so the
 * review screen keeps approve, reject and select exactly as they were.
 *
 * Only the target and the reason are sent — the snapshot of what was flagged is
 * frozen server-side. Domain failure: `VARIANT_NOT_FOUND` (404).
 */
export function flagVariant(
  businessId: string,
  requestId: string,
  body: FlagCreate,
): Promise<FlagRead> {
  return apiClient.post<FlagRead>(`${requestsPath(businessId)}/${requestId}/flag`, { body });
}

/**
 * Records that this request's caption went out by hand — the user's own claim
 * about the outside world, for every platform we cannot publish to ourselves.
 * Nothing is sent to a social platform by calling it. A LinkedIn post published
 * through {@link publishRequest} writes its own row instead.
 *
 * One record per request. Domain failures: `REQUEST_NOT_COMPLETE` (409),
 * `ALREADY_PUBLISHED` (409) — the second is not really a failure, since the
 * request is in exactly the state the caller wanted.
 */
export function recordPublishedPost(
  businessId: string,
  requestId: string,
  body: PublishedPostCreate = {},
): Promise<PublishedPostRead> {
  return apiClient.post<PublishedPostRead>(`${requestsPath(businessId)}/${requestId}/published`, {
    body,
  });
}

/**
 * Asks for this request to be published to the platform it was written for.
 *
 * Answers 202 with a `pending` job: the posting itself runs in an Arq worker,
 * because it is several LinkedIn calls (an upload per image, then the post) and
 * a web deploy must not kill a half-uploaded post. The caller polls
 * {@link getPublishJob} rather than awaiting anything.
 *
 * Guards, in the order the backend checks them: `INTEGRATION_DISABLED` (503,
 * no LinkedIn app on this server), `REQUEST_NOT_COMPLETE`, `PLATFORM_MISMATCH`
 * (the request was not written for LinkedIn), `LINKEDIN_NOT_CONNECTED`,
 * `LINKEDIN_RECONNECT_REQUIRED` (the token expired), `ALREADY_PUBLISHED`,
 * `PUBLISH_IN_PROGRESS`, `EXPORT_NOT_READY` (a slide is still rendering).
 */
export function publishRequest(businessId: string, requestId: string): Promise<PublishJobRead> {
  return apiClient.post<PublishJobRead>(`${requestsPath(businessId)}/${requestId}/publish`);
}

/**
 * The latest publish job for this request, or null when it has never been
 * published from here. The poll target while one is in flight.
 */
export function getPublishJob(
  businessId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<PublishJobRead | null> {
  return (
    apiClient
      .get<PublishJobRead | null>(`${requestsPath(businessId)}/${requestId}/publish-job`, {
        signal,
      })
      // Normalised: "no job" is a JSON null on the wire, but a 204 would reach the
      // caller as undefined, and one absent-value shape is enough to reason about.
      .then((job) => job ?? null)
  );
}

/**
 * Path of the export bundle: the chosen caption plus the imagery, as a zip.
 *
 * A path rather than a call, because the response is bytes and a
 * `Content-Disposition` — {@link downloadApiFile} takes it from here. Nothing is
 * rendered or regenerated by asking for it; a request with a slide still
 * re-rendering answers 409 `EXPORT_NOT_READY`. A generated single carries one
 * PNG, a carousel one per slide.
 */
export function exportRequestPath(businessId: string, requestId: string): string {
  return `${requestsPath(businessId)}/${requestId}/export`;
}

/** Filename the export falls back to when `Content-Disposition` does not arrive. */
export function exportFilename(platform: string, requestId: string): string {
  return `outpost-${platform}-${requestId.slice(0, 8)}.zip`;
}

/**
 * Progress stream URL. Top-level path, not business-scoped: the minted token
 * already pins the subscription to one request.
 */
export function generationEventsUrl(requestId: string, token: string): string {
  const url = new URL(`${API_BASE_URL}/api/v1/generation-requests/${requestId}/events`);
  url.searchParams.set('token', token);
  return url.toString();
}
